import {
	AddressType,
	useCreateOrganizationAddressMutation,
	useUpdateOrganizationAddressMutation,
} from '@codezo/graphql';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import {
	AddressForm,
	EMPTY_ADDRESS,
	validateAddress,
	type AddressData,
	type AddressErrors,
} from '../../components/ui/address-form';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { SelectPopover } from '../../components/ui/select-popover';
import { Switch } from '../../components/ui/switch';
import { ADDRESS_TYPE_LABEL, type OrganizationAddress } from './address-utils';

const TYPE_OPTIONS = Object.values(AddressType).map((value) => ({
	value,
	label: ADDRESS_TYPE_LABEL[value],
}));

interface AddressModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	organizationId: string;
	/** The address being edited. `null` adds a new one. */
	address: OrganizationAddress | null;
	/**
	 * True when the client has no addresses at all — the first one is primary
	 * whatever the toggle says, so it's shown on and locked rather than hidden.
	 */
	isFirstAddress?: boolean;
	onSuccess: () => void;
}

export function AddressModal({
	open,
	onOpenChange,
	organizationId,
	address,
	isFirstAddress,
	onSuccess,
}: AddressModalProps) {
	const [form, setForm] = useState<AddressData>(EMPTY_ADDRESS);
	const [name, setName] = useState('');
	const [addressType, setAddressType] = useState<AddressType>(AddressType.Shipping);
	const [isPrimary, setIsPrimary] = useState(false);
	const [errors, setErrors] = useState<AddressErrors>({});
	// Whether a state is required, and what that country calls it — the form knows,
	// this doesn't, so it reports the rules up.
	const [countryRules, setCountryRules] = useState({ requireState: false, stateLabel: 'State' });

	const [createAddress, { loading: creating }] = useCreateOrganizationAddressMutation();
	const [updateAddress, { loading: updating }] = useUpdateOrganizationAddressMutation();
	const loading = creating || updating;

	// Seeded on open, not on mount — the modal instance is reused for every row,
	// so an edit opened after another must re-read the address it was given.
	useEffect(() => {
		if (!open) return;
		setForm(
			address
				? {
						addressLine1: address.addressLine1,
						addressLine2: address.addressLine2 ?? '',
						city: address.city,
						stateCode: address.stateCode ?? '',
						postalCode: address.postalCode,
						countryCode: address.countryCode,
						googlePlaceId: address.googlePlaceId ?? '',
						latitude: address.latitude ?? null,
						longitude: address.longitude ?? null,
					}
				: EMPTY_ADDRESS
		);
		setName(address?.name ?? '');
		setAddressType(address?.addressType ?? AddressType.Shipping);
		setIsPrimary(address?.isPrimary ?? !!isFirstAddress);
		setErrors({});
	}, [open, address, isFirstAddress]);

	// Identity-stable, or the form's effect would re-report on every render.
	const handleCountryRules = useCallback(
		(rules: { requireState: boolean; stateLabel: string }) => setCountryRules(rules),
		[]
	);

	const handleSubmit = async () => {
		// Checked on submit rather than by disabling the button: a dead button
		// never says which field is missing.
		const found = validateAddress(form, countryRules);
		setErrors(found);
		if (Object.keys(found).length > 0) return;

		const input = {
			addressLine1: form.addressLine1.trim(),
			addressLine2: form.addressLine2.trim() || undefined,
			city: form.city.trim(),
			stateCode: form.stateCode.trim() || undefined,
			postalCode: form.postalCode.trim(),
			countryCode: form.countryCode.trim().toUpperCase(),
			googlePlaceId: form.googlePlaceId || undefined,
			latitude: form.latitude ?? undefined,
			longitude: form.longitude ?? undefined,
			name: name.trim() || undefined,
			addressType,
			// The first address has to be primary — nothing else could be.
			isPrimary: isPrimary || !!isFirstAddress,
		};

		try {
			if (address) {
				await updateAddress({ variables: { id: address.id, input } });
				toast.success('Address updated');
			} else {
				await createAddress({ variables: { organizationId, input } });
				toast.success('Address added');
			}
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to save address');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{address ? 'Edit address' : 'Add address'}</DialogTitle>
					<DialogDescription>
						{address
							? 'Update where this client ships and bills.'
							: 'Add a shipping or billing address for this client.'}
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
								<Field label="Label">
									<Input
										value={name}
										onChange={(e) => setName(e.target.value)}
										placeholder="e.g. Warehouse"
									/>
								</Field>
								<Field label="Type">
									<SelectPopover
										value={addressType}
										onChange={(v) => setAddressType(v as AddressType)}
										options={TYPE_OPTIONS}
										placeholder="Select type"
									/>
								</Field>
							</div>

							<AddressForm
								value={form}
								onChange={(next) => {
									setForm(next);
									// Clear a message the moment its field is filled in,
									// rather than making the admin submit again to find out.
									setErrors((prev) =>
										Object.keys(prev).length > 0
											? validateAddress(next, countryRules)
											: prev
									);
								}}
								errors={errors}
								onCountryRulesChange={handleCountryRules}
							/>

							<div className="flex items-center justify-between gap-4 border-t border-line-1 pt-4">
								<div className="min-w-0">
									<div className="text-sm font-medium text-text-1">
										Primary address
									</div>
									<div className="text-xs text-text-4">
										{isFirstAddress
											? 'The first address is always the primary one.'
											: 'Used as the default for new quotes and orders.'}
									</div>
								</div>
								<Switch
									checked={isPrimary || !!isFirstAddress}
									onCheckedChange={setIsPrimary}
									// An already-primary address can't be un-primaried from
									// here — the client would be left without one. Making
									// another address primary is what moves it.
									disabled={isFirstAddress || address?.isPrimary}
								/>
							</div>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={loading}>
							{loading ? 'Saving…' : address ? 'Save changes' : 'Add address'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
